import React from 'react';

import { ApiClientHeaders, ApiClientManager } from '@api/consumerloans-public-api';
import { LoadReactWidget } from '@bcp-channels-sharedcomponents-reactjs/core';
import {
	FormatHelper,
	GtmHelper,
	LoggerHelper,
	TranslationHelper,
} from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { useDidMount, useWillMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { Company } from '@bcp-channels-sharedcomponents-reactjs/core/dist/types';
import { ApiConfig, ContactButtonConfig, LoggerConfig, getAppSettings } from '@configs';
import { TranslationValues } from '@constants';
import { css, Global } from '@emotion/react';
import { getServiceInputMetadata } from '@helpers';
import { useGetCurrentBreakpoint, useModal } from '@hooks';
import { useFetchInit } from '@services';
import { useBaseStore } from '@store';
import { BaseSessionInfo, BaseSessionInfoLoanType } from '@types';

import { App } from './app';
import { useHideScroll } from './hooks/useLockScroll';

export const globalLoaderKey = 'globalLoader';

interface ConfigureAppProps {
	sessionInfo: BaseSessionInfo;
}

export const ConfigureApp: React.FC<ConfigureAppProps> = ({ sessionInfo }) => {
	const [initialized, setInitialized] = React.useState(true);
	const setBaseState = useBaseStore((state) => state.setBaseState);
	const { disable } = useHideScroll();
	const { show: showModal } = useModal('genericError');
	const { currentView } = useGetCurrentBreakpoint();
	const isDesktop = currentView === 'bigView';

	const { fetch } = useFetchInit(sessionInfo);

	useDidMount(() => {
		fetch({});
	});

	useWillMount(() => {
		setBaseState({
			sessionInfo,
			appSettings: getAppSettings(sessionInfo),
		});

		TranslationHelper.init({
			company: sessionInfo.company?.toLowerCase() === 'ab' ? Company.ab : Company.bcp,
			language: FormatHelper.formatLanguageCode(sessionInfo.language),
			siteName: TranslationValues.siteName,
			listName: TranslationValues.listName,
			keyGroup: TranslationValues.keyGroups.global,
			httpRequest: {
				url: `${ApiConfig.url}api/rpc/getSharepointResources`,
				disableAutomaticRequest: false,
				headers: {
					accept: ApiConfig.headers.accept,
					authorization: `Bearer ${sessionInfo.api.tokenOAuth}`,
					'ocp-apim-subscription-key': sessionInfo.api.subscriptionKey,
					'content-type': ApiConfig.headers.contentType,
				} as any,
				buildBody: (input) => ({
					data: { ...input, metadata: getServiceInputMetadata(sessionInfo) },
				}),
				loading: {
					hide: (): void => {
						LoggerHelper.log('Translation', 'Translation > init > hideLoading');
					},
					show: (): void => {
						LoggerHelper.log('Translation', 'Translation > init > showLoading');
					},
				},
				onError: (err: any) => {
					LoggerHelper.log('Translation', 'Translation > init > onError', err, sessionInfo);
					disable();
					showModal();

					setInitialized(false);
				},
				onSuccess: (res: any): any => {
					LoggerHelper.log('Translation', 'Translation > init > onSuccess', res);
					setInitialized(true);
					return TranslationHelper.transformArrayToObject(res.data.resources, {
						propNameKey: 'key',
						propNameValue: 'value',
					});
				},
			},
		});

		// cSpell: disable
		// eslint-disable-next-line
		GtmHelper.init({ item_category: 'chMoratorium', item_type: 'chMoratorium' });
		// cSpell: enable

		ApiClientManager.init(
			ApiConfig.url,
			{
				accept: ApiConfig.headers.accept,
				authorization: `Bearer ${sessionInfo.api.tokenOAuth}`,
				'ocp-apim-subscription-key': sessionInfo.api.subscriptionKey,
				'content-type': ApiConfig.headers.contentType,
			} as ApiClientHeaders,
			LoggerConfig.log
		);
	});

	return (
		<>
			{initialized && (
				<>
					<Global
						styles={css`
							input,
							textarea,
							select {
								box-shadow: none !important;
							}
						`}
					/>
					{sessionInfo.company?.toLowerCase() === 'ab' && (
						<LoadReactWidget
							url={
								sessionInfo.company?.toLowerCase() === Company.ab
									? ContactButtonConfig.urlAb
									: ContactButtonConfig.url
							}
							/* eslint-disable sonarjs/cognitive-complexity */
							onSuccess={({ htmlElement }) => {
								window.ReactWidgetsContactHub?.api?.loadWidgetApp({
									contextValues: {
										...sessionInfo,
										initialPosition: isDesktop
											? undefined
											: ({ bottom: '70%', right: '1px' } as any),
										language: FormatHelper.formatLanguageCode(sessionInfo.language),
										channel: 'Site',
										segment: 'Retail',
										/* cSpell:disable */
										journey:
											sessionInfo.journeyData.loanType === BaseSessionInfoLoanType.AL
												? 'SIMULADOR_AUTO_USADOS'
												: sessionInfo.journeyData.loanType === BaseSessionInfoLoanType.RENEN
												? 'SIMULADOR_ENERGIAS_RENOVAVEIS'
												: sessionInfo.journeyData.loanType === BaseSessionInfoLoanType.ALEV
												? 'SIMULADOR_AUTO_ELETRICOS'
												: sessionInfo.journeyData.loanType === BaseSessionInfoLoanType.HEALTH
												? 'SIMULADOR_SAUDE'
												: sessionInfo.journeyData.loanType === BaseSessionInfoLoanType.EDUCA
												? 'SIMULADOR_FORMACAO'
												: sessionInfo.journeyData.loanType === BaseSessionInfoLoanType.HOME
												? 'SIMULADOR_LAR'
												: sessionInfo.journeyData.loanType === BaseSessionInfoLoanType.CONSTR
												? 'SIMULADOR_OBRAS'
												: 'SIMULADOR_CP',
										/* eslint-enable sonarjs/cognitive-complexity */
										step: 'Simulação',
										/* cSpell:enable */
										handlers: {
											onError: (error) => {
												LoggerHelper.error('ContactHub', error);
											},
										},
									},
									targetHtmlElement: htmlElement,
								});
							}}
						/>
					)}
					<App />
				</>
			)}
		</>
	);
};
